// components/GuildHashtags.js - 길드 해시태그 표시 컴포넌트
import React from 'react';

function GuildHashtags({ 
  hashtags = [], 
  maxDisplay = 5, 
  size = 'medium', // 'small', 'medium', 'large'
  colorScheme = 'gradient', // 'gradient', 'solid', 'outline'
  onClick = null, // 해시태그 클릭 핸들러
  className = '',
  style = {}
}) {
  if (!hashtags || hashtags.length === 0) {
    return null;
  }

  // 크기별 스타일
  const sizeStyles = {
    small: {
      fontSize: 10,
      padding: '3px 8px',
      borderRadius: 12,
      gap: 4
    },
    medium: {
      fontSize: 12,
      padding: '6px 12px',
      borderRadius: 16,
      gap: 6
    },
    large: {
      fontSize: 14,
      padding: '8px 16px',
      borderRadius: 20,
      gap: 8
    }
  };

  // 색상 스키마별 스타일
  const colorSchemes = {
    gradient: {
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      color: '#fff',
      border: 'none',
      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
    },
    solid: {
      background: '#667eea',
      color: '#fff',
      border: 'none',
      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
    },
    outline: {
      background: 'transparent',
      color: '#667eea',
      border: '1px solid #667eea',
      boxShadow: 'none'
    }
  };

  const currentSizeStyle = sizeStyles[size] || sizeStyles.medium;
  const currentColorStyle = colorSchemes[colorScheme] || colorSchemes.gradient;

  // 표시할 해시태그와 숨겨진 개수
  const displayTags = hashtags.slice(0, maxDisplay);
  const hiddenCount = hashtags.length - maxDisplay;

  const handleTagClick = (tag, event) => {
    if (onClick) {
      event.preventDefault();
      event.stopPropagation();
      onClick(tag);
    }
  };

  return (
    <div 
      className={className}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: currentSizeStyle.gap,
        alignItems: 'center',
        ...style
      }}
    >
      {displayTags.map((tag, index) => (
        <span
          key={index}
          onClick={onClick ? (e) => handleTagClick(tag, e) : undefined}
          style={{
            ...currentSizeStyle,
            ...currentColorStyle,
            fontWeight: 600,
            cursor: onClick ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}
          onMouseEnter={(e) => {
            if (onClick) {
              e.target.style.transform = 'translateY(-1px) scale(1.05)';
              e.target.style.boxShadow = colorScheme === 'gradient' || colorScheme === 'solid' 
                ? '0 4px 12px rgba(102, 126, 234, 0.5)'
                : '0 2px 8px rgba(102, 126, 234, 0.3)';
            }
          }}
          onMouseLeave={(e) => {
            if (onClick) {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = currentColorStyle.boxShadow;
            }
          }}
          title={`#${tag} 해시태그`}
        >
          #{tag}
        </span>
      ))}
      
      {hiddenCount > 0 && (
        <span
          style={{
            ...currentSizeStyle,
            background: 'rgba(0,0,0,0.1)',
            color: '#6b7280',
            border: '1px solid rgba(0,0,0,0.1)',
            fontWeight: 500,
            cursor: 'default'
          }}
          title={`+${hiddenCount}개의 해시태그가 더 있습니다`}
        >
          +{hiddenCount}
        </span>
      )}
    </div>
  );
}

// 해시태그 검색/필터링을 위한 유틸리티 함수들
export const hashtagUtils = {
  // 해시태그로 길드 필터링
  filterGuildsByHashtag: (guilds, hashtag) => {
    return guilds.filter(guild => 
      guild.hashtags && guild.hashtags.some(tag => 
        tag.toLowerCase().includes(hashtag.toLowerCase())
      )
    );
  },

  // 모든 길드의 해시태그 추출 (빈도순 정렬)
  extractAllHashtags: (guilds) => {
    const tagCounts = {};
    
    guilds.forEach(guild => {
      if (guild.hashtags) {
        guild.hashtags.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      }
    });

    return Object.entries(tagCounts)
      .sort(([,a], [,b]) => b - a)
      .map(([tag, count]) => ({ tag, count }));
  },

  // 추천 해시태그 (많이 사용되는 태그)
  getPopularHashtags: (guilds, limit = 10) => {
    const allTags = hashtagUtils.extractAllHashtags(guilds);
    return allTags.slice(0, limit).map(item => item.tag);
  },

  // 해시태그 유효성 검사
  validateHashtag: (tag) => {
    const cleanTag = tag.replace('#', '').trim();
    
    // 2-20자, 한글/영문/숫자만 허용
    const isValid = /^[가-힣a-zA-Z0-9]{2,20}$/.test(cleanTag);
    
    return {
      isValid,
      cleanTag,
      errors: isValid ? [] : [
        cleanTag.length < 2 && '최소 2자 이상',
        cleanTag.length > 20 && '최대 20자 이하',
        !/^[가-힣a-zA-Z0-9]+$/.test(cleanTag) && '한글, 영문, 숫자만 사용 가능'
      ].filter(Boolean)
    };
  }
};

export default GuildHashtags;